"use client"

import { Text } from "@/elements"
import { AvatarSize } from "@/elements/Avatar/Avatar"
import { AvatarGroup, AvatarGroupItem } from "@/elements/AvatarGroup"
import { FragmentType, graphql } from "@/gql"
import { getFragmentData } from "@/gql-clients/experimental/getFragmentData"
import { TextVariant } from "@/theme"
import type { NovaSession } from "@/types"
import clsx from "clsx"
import pluralize from "pluralize"
import { FC, useState } from "react"
import { ConnectionsModal } from "./ConnectionsModal"

const ConnectionsPreviewFragment = graphql(`
  fragment ConnectionsPreview on User {
    id
    mutualsPreview: friendPreviews(first: 3, type: MUTUALS) @include(if: $isCreativeViewer) {
      totalCount
      edges {
        node {
          avatar72w: avatarUrl(width: 72)
          avatarInitials
        }
      }
    }
    connectionsPreview: friendPreviews(first: 3, type: ALL) {
      totalCount
      edges {
        node {
          avatar72w: avatarUrl(width: 72)
          avatarInitials
        }
      }
    }
  }
`)

const ConnectionsPreviewV1Fragment = graphql(`
  fragment ConnectionsPreviewV1 on User {
    id
    mutualsPreview: connections(first: 3, type: MUTUAL) @include(if: $isCreativeViewer) {
      totalCount
      edges {
        node {
          avatar72w: avatarUrl(width: 72)
          avatarInitials
        }
      }
    }
    connectionsPreview: connections(first: 3, type: ALL) {
      totalCount
      edges {
        node {
          avatar72w: avatarUrl(width: 72)
          avatarInitials
        }
      }
    }
  }
`)

interface Props {
  user:
    | FragmentType<typeof ConnectionsPreviewFragment>
    | FragmentType<typeof ConnectionsPreviewV1Fragment>
    | null
    | undefined
  avatarGroupItemClassName?: string
  textLeft?: boolean
  textVariant?: TextVariant
  serverSession: NovaSession | null
  avatarBorderWidth?: 1 | 2
  avatarSize?: AvatarSize
  avatarSizeNumber?: number
  className?: string
}

export const ConnectionsPreview: FC<Props> = props => {
  const { avatarGroupItemClassName, textVariant = "body1", textLeft = false, serverSession } = props
  const [openModa, setOpenModal] = useState(false)
  /* 
  temp handler until roleCount and typeCount is added to CreativeWorkProjectConnections
  so that we can use v2 api on the portfolio page
  */
  const userV1 = getFragmentData(
    ConnectionsPreviewV1Fragment,
    props.user as FragmentType<typeof ConnectionsPreviewV1Fragment>
  )
  const userV2 = getFragmentData(
    ConnectionsPreviewFragment,
    props.user as FragmentType<typeof ConnectionsPreviewFragment>
  )
  const user = userV1 || userV2
  /* end temp handler */

  const isCreativeViewer = serverSession?.account === "creative"
  const userIsViewer = serverSession?.user?.id === user?.id

  const shouldShowMutuals =
    isCreativeViewer && user?.mutualsPreview?.totalCount && user?.mutualsPreview?.totalCount >= 1

  const connections = shouldShowMutuals ? user?.mutualsPreview : user?.connectionsPreview
  const connectionsCount = connections?.totalCount
  const connectionsEdges = connections?.edges

  // if the user is not logged in, do we want to share this information. Not sure if it's possible anywhere... just leaving as a precaution
  if (!serverSession?.accessToken || userIsViewer) {
    return null
  }

  const avatarGroupItems =
    connectionsEdges
      ?.map(edge => {
        const node = edge?.node
        if (node) {
          return {
            initials: node.avatarInitials,
            src: node.avatar72w,
          }
        }
        return null
      })
      .filter((item): item is AvatarGroupItem => item !== null) ?? []

  const displayText = `${connectionsCount} ${pluralize(shouldShowMutuals ? "Mutual" : "Connection", connectionsCount || 0)}`

  return (
    <>
      <div className="hidden" onClick={e => e.stopPropagation()}>
        <ConnectionsModal userId={user?.id} open={openModa} onOpenChange={setOpenModal} />
      </div>
      <div
        className={clsx("flex flex-shrink-0 cursor-pointer flex-row items-center overflow-hidden", props.className)}
        onClick={e => {
          e.stopPropagation()
          e.preventDefault()
          setOpenModal(true)
        }}>
        {textLeft && (
          <Text variant={textVariant} className="overflow-hidden text-ellipsis whitespace-nowrap pr-2 text-fg-2">
            {displayText}
          </Text>
        )}
        {Boolean(avatarGroupItems && avatarGroupItems.length > 0) && (
          <div className="mr-2">
            <AvatarGroup
              items={avatarGroupItems}
              itemClassName={avatarGroupItemClassName}
              avatarSize={props.avatarSize}
              avatarSizeNumber={props.avatarSizeNumber}
              borderWidth={props.avatarBorderWidth}
            />
          </div>
        )}
        {!textLeft && (
          <Text variant={textVariant} className="overflow-hidden text-ellipsis whitespace-nowrap text-fg-2">
            {displayText}
          </Text>
        )}
      </div>
    </>
  )
}
